import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { memoize } from 'lodash-es';

import type { RequiredBy } from '@iconik-io/core/src/types';
import type {
  AclsGroupACLSchema,
  AclsUserACLSchema,
} from '@iconik-io/iconik-api/src/services/acls';

import {
  ACL_TYPE_INHERITED,
  ACL_TYPE_OWN,
  ACL_TYPE_PROPAGATED,
} from './constants';
import type {
  AclType,
  AddToGroupPayload,
  AddToUserPayload,
  CommonAclsAppState,
  CommonAclsPrefix,
  DeleteFromGroupPayload,
  DeleteFromUserPayload,
  EntityGroupAcl,
  EntityUserAcl,
  SetCurrentPayload,
  SetPayload,
} from './types';

export const initialState: CommonAclsAppState = {
  entityUsersAcls: {},
  entityGroupsAcls: {},
  entityCurrentUserAcls: {},
};

function toEntityUserAcls(
  acls: AclsUserACLSchema[],
  type: AclType,
): EntityUserAcl[] {
  // The schema types the id as optional, though an ACL without one names
  // nobody and nothing downstream could render it.
  return acls
    .filter((acl): acl is RequiredBy<AclsUserACLSchema, 'user_id'> =>
      Boolean(acl.user_id),
    )
    .map(({ user_id, permissions, object_type, object_key }) => ({
      user_id,
      permissions,
      type,
      objectType: object_type,
      objectKey: object_key,
    }));
}

function toEntityGroupAcls(
  acls: AclsGroupACLSchema[],
  type: AclType,
): EntityGroupAcl[] {
  // See toEntityUserAcls.
  return acls
    .filter((acl): acl is RequiredBy<AclsGroupACLSchema, 'group_id'> =>
      Boolean(acl.group_id),
    )
    .map(({ group_id, permissions, object_type, object_key }) => ({
      group_id,
      permissions,
      type,
      objectType: object_type,
      objectKey: object_key,
    }));
}

const isNonInheritedSource =
  (prefix: CommonAclsPrefix, id: string) =>
  (item: AclsUserACLSchema | AclsGroupACLSchema) =>
    item.object_type !== prefix || item.object_key !== id;

export const getCommonAclsSlice = memoize((prefix: CommonAclsPrefix) =>
  createSlice({
    name: `${prefix}_acls`,
    initialState,
    reducers: {
      aclsAddToUser: (state, action: PayloadAction<AddToUserPayload>) => {
        const { id, permissions, user_id, type } = action.payload;
        const acls = state.entityUsersAcls[id];
        const existing = acls?.find(
          (item) => item.user_id === user_id && item.type === type,
        );
        if (existing) {
          existing.permissions = permissions;
          return;
        }
        state.entityUsersAcls[id] = [
          { user_id, permissions, type },
          ...(acls ?? []),
        ];
      },

      aclsAddToGroup: (state, action: PayloadAction<AddToGroupPayload>) => {
        const { id, permissions, group_id, type } = action.payload;
        const acls = state.entityGroupsAcls[id];
        const existing = acls?.find(
          (item) => item.group_id === group_id && item.type === type,
        );
        if (existing) {
          existing.permissions = permissions;
          return;
        }
        state.entityGroupsAcls[id] = [
          { group_id, permissions, type },
          ...(acls ?? []),
        ];
      },

      aclsDeleteFromUser: (
        state,
        action: PayloadAction<DeleteFromUserPayload>,
      ) => {
        const { id, user_id, type } = action.payload;
        state.entityUsersAcls[id] = (state.entityUsersAcls[id] ?? []).filter(
          (item) => item.user_id !== user_id || item.type !== type,
        );
      },

      aclsDeleteFromGroup: (
        state,
        action: PayloadAction<DeleteFromGroupPayload>,
      ) => {
        const { id, group_id, type } = action.payload;
        state.entityGroupsAcls[id] = (state.entityGroupsAcls[id] ?? []).filter(
          (item) => item.group_id !== group_id || item.type !== type,
        );
      },

      aclsSet: (state, action: PayloadAction<SetPayload>) => {
        const { id, acl } = action.payload;
        const {
          users_acl,
          propagating_users_acl,
          inherited_users_acl,
          groups_acl,
          propagating_groups_acl,
          inherited_groups_acl,
        } = acl;

        // Inherited acls are skipped when their source is the current entity
        state.entityUsersAcls[id] = [
          ...toEntityUserAcls(users_acl ?? [], ACL_TYPE_OWN),
          ...toEntityUserAcls(propagating_users_acl ?? [], ACL_TYPE_PROPAGATED),
          ...toEntityUserAcls(
            (inherited_users_acl ?? []).filter(
              isNonInheritedSource(prefix, id),
            ),
            ACL_TYPE_INHERITED,
          ),
        ];

        state.entityGroupsAcls[id] = [
          ...toEntityGroupAcls(groups_acl ?? [], ACL_TYPE_OWN),
          ...toEntityGroupAcls(
            propagating_groups_acl ?? [],
            ACL_TYPE_PROPAGATED,
          ),
          ...toEntityGroupAcls(
            (inherited_groups_acl ?? []).filter(
              isNonInheritedSource(prefix, id),
            ),
            ACL_TYPE_INHERITED,
          ),
        ];
      },

      aclsSetCurrent: (state, action: PayloadAction<SetCurrentPayload>) => {
        const { id, permissions } = action.payload;
        state.entityCurrentUserAcls[id] = permissions;
      },

      aclsClear: () => initialState,

      aclsSetEdit: (state, action: PayloadAction<string | undefined>) => {
        state.edit = action.payload;
      },
    },
  }),
);

export type CommonAclsActionTypes = ReturnType<
  typeof getCommonAclsSlice
>['actions'];
